import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from '../Layout/DashboardLayout';
import LoadingSpinner from '../Common/LoadingSpinner';
import { requestHelpers } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './Automation.css';

const Automation = () => {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFlowBuilder, setShowFlowBuilder] = useState(false);
  const [currentFlow, setCurrentFlow] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedNode, setDraggedNode] = useState(null);
  const [connections, setConnections] = useState([]);
  const [pendingConnection, setPendingConnection] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const { user } = useAuth();
  const [businessNumbers, setBusinessNumbers] = useState([]);
  const [whatsappFlows, setWhatsappFlows] = useState([]);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [availableMedia, setAvailableMedia] = useState([]);

  // Dragging state for existing nodes
  const [isMovingNode, setIsMovingNode] = useState(false);
  const [movingNodeId, setMovingNodeId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [isAnalyticsMode, setIsAnalyticsMode] = useState(false);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  // Panning state
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  const [flowData, setFlowData] = useState({
    nodes: [],
    connections: []
  });

  const canvasRef = useRef(null);
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });
  const [activeFunnelFilters, setActiveFunnelFilters] = useState([]); // Funnel filtering state
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [showJourneyModal, setShowJourneyModal] = useState(false);

  // Available node types for the flow builder
  const nodeTypes = [
    {
      type: 'trigger',
      category: 'Triggers',
      items: [
        // {
        //   id: 'contact_added',
        //   name: 'Contact Added',
        //   icon: 'fas fa-user-plus',
        //   description: 'Triggers when a new contact is added',
        //   inputs: 0,
        //   outputs: 1
        // },
        // {
        //   id: 'campaign_started',
        //   name: 'Campaign Started',
        //   icon: 'fas fa-play',
        //   description: 'Triggers when a campaign starts',
        //   inputs: 0,
        //   outputs: 1
        // },
        {
          id: 'message_received',
          name: 'Message Received',
          icon: 'fas fa-envelope',
          description: 'Triggers when a message is received',
          inputs: 0,
          outputs: 1
        },
        {
          id: 'webhook_received',
          name: 'Webhook',
          icon: 'fas fa-link',
          description: 'Triggers when webhook is called',
          inputs: 0,
          outputs: 1
        }
      ]
    },
    // {
    //   type: 'trigger',
    //   category: 'Shopify Events',
    //   items: [
    //     {
    //       id: 'shopify_abandoned_cart',
    //       name: 'Abandoned Cart',
    //       icon: 'fas fa-shopping-cart',
    //       description: 'Triggers when a cart is abandoned',
    //       inputs: 0,
    //       outputs: 1
    //     },
    //     {
    //       id: 'shopify_order_created',
    //       name: 'Order Created',
    //       icon: 'fas fa-box',
    //       description: 'Triggers on new shopify order',
    //       inputs: 0,
    //       outputs: 1
    //     },
    //     {
    //       id: 'shopify_fulfillment_created',
    //       name: 'Order Shipped',
    //       icon: 'fas fa-shipping-fast',
    //       description: 'Triggers when order is fulfilled',
    //       inputs: 0,
    //       outputs: 1
    //     }
    //   ]
    // },
    {
      type: 'action',
      category: 'Actions',
      items: [
        {
          id: 'send_message',
          name: 'Send Message',
          icon: 'fas fa-paper-plane',
          description: 'Send a WhatsApp message',
          inputs: 1,
          outputs: 1
        },
        // {
        //   id: 'add_to_campaign',
        //   name: 'Add to Campaign',
        //   icon: 'fas fa-bullhorn',
        //   description: 'Add contact to a campaign',
        //   inputs: 1,
        //   outputs: 1
        // },
        {
          id: 'update_contact',
          name: 'Update Contact',
          icon: 'fas fa-user-edit',
          description: 'Update contact information',
          inputs: 1,
          outputs: 1
        },
        {
          id: 'add_tag',
          name: 'Add Tag',
          icon: 'fas fa-tag',
          description: 'Add tag to contact',
          inputs: 1,
          outputs: 1
        },
        {
          id: 'remove_tag',
          name: 'Remove Tag',
          icon: 'fas fa-tag',
          description: 'Remove tag from contact',
          inputs: 1,
          outputs: 1
        },
        {
          id: 'wait_delay',
          name: 'Wait/Delay',
          icon: 'fas fa-clock',
          description: 'Wait for specified time',
          inputs: 1,
          outputs: 1
        },
        {
          id: 'interactive_buttons',
          name: 'Buttons/Quick Reply',
          icon: 'fas fa-th-large',
          description: 'Send message with buttons',
          inputs: 1,
          outputs: 'dynamic'
        },
        {
          id: 'list_messages',
          name: 'List Message',
          icon: 'fas fa-list-ul',
          description: 'Send a list menu (max 10 rows)',
          inputs: 1,
          outputs: 'dynamic'
        },
        // {
        //   id: 'send_flow',
        //   name: 'Send WhatsApp Flow',
        //   icon: 'fas fa-list-check',
        //   description: 'Auto-reply with an interactive WhatsApp Flow',
        //   inputs: 1,
        //   outputs: 1
        // },
        {
          id: 'send_template',
          name: 'Send Template',
          icon: 'fas fa-id-card',
          description: 'Send approved WhatsApp template',
          inputs: 1,
          outputs: 1
        }
      ]
    },
    {
      type: 'condition',
      category: 'Conditions',
      items: [
        {
          id: 'if_condition',
          name: 'If Condition',
          icon: 'fas fa-code-branch',
          description: 'Branch based on condition',
          inputs: 1,
          outputs: 2
        },
        {
          id: 'contact_has_tag',
          name: 'Contact Has Tag',
          icon: 'fas fa-tags',
          description: 'Check if contact has specific tag',
          inputs: 1,
          outputs: 2
        },
        {
          id: 'message_contains',
          name: 'Message Contains',
          icon: 'fas fa-search',
          description: 'Check if message contains text',
          inputs: 1,
          outputs: 2
        }
      ]
    }
  ];

  useEffect(() => {
    fetchFlows();
    fetchBusinessNumbers();
    fetchWhatsAppFlows();
    fetchTemplates();
    fetchCampaigns();
    fetchMedia();
  }, []);

  const fetchWhatsAppFlows = async () => {
    try {
      const response = await requestHelpers.get('/whatsapp-flows');
      setWhatsappFlows(response.data || []);
    } catch (err) {
      console.error('Failed to fetch whatsapp flows for automation', err);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await requestHelpers.get('/templates', { params: { status: 'APPROVED', limit: 1000 } });
      // Backend returns { success: true, data: { templates: [], total: 0 } }
      setAvailableTemplates(response.data?.templates || []);
    } catch (err) {
      console.error('Failed to fetch templates for automation', err);
      setAvailableTemplates([]);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await requestHelpers.get('/campaigns');
      setAvailableCampaigns(response.data || []);
    } catch (err) {
      console.error('Failed to fetch campaigns for automation', err);
    }
  };

  const fetchMedia = async () => {
    try {
      const response = await requestHelpers.get('/media');
      setAvailableMedia(response.data || []);
    } catch (error) {
      console.error('Failed to fetch media:', error);
    }
  };

  const fetchBusinessNumbers = async () => {
    try {
      if (user) {
        const numbers = [];
        if (user.whatsappNumber) numbers.push(user.whatsappNumber);

        if (user.whatsappAccounts && Array.isArray(user.whatsappAccounts)) {
          user.whatsappAccounts.forEach(acc => {
            if (acc.whatsappNumber && !numbers.includes(acc.whatsappNumber)) {
              numbers.push(acc.whatsappNumber);
            }
          });
        }

        setBusinessNumbers(numbers);
      }
    } catch (error) {
      console.error('Failed to set business numbers', error);
    }
  };

  const fetchFlows = async () => {
    try {
      setLoading(true);
      const response = await requestHelpers.get('/flows');
      setFlows(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch automation flows');
    } finally {
      setLoading(false);
    }
  };

  const fetchFlowAnalytics = async (flowId, funnelFilters = []) => {
    try {
      setLoadingAnalytics(true);
      
      let url = `/flows/${flowId}/analytics`;
      if (funnelFilters && funnelFilters.length > 0) {
        url = `/flows/${flowId}/journeys?filters=${encodeURIComponent(JSON.stringify(funnelFilters))}`;
      }
      
      const res = await requestHelpers.get(url);
      
      if (funnelFilters.length > 0) {
        // If we fetched filtered journeys, update just the journeys list
        setAnalyticsData(prev => ({ ...prev, recentJourneys: res.data }));
      } else {
        // Initial full analytics load
        setAnalyticsData(res.data);
      }
      
      setIsAnalyticsMode(true);
    } catch (err) {
      toast.error('Failed to load flow analytics');
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const handleAnalyzeFlow = (flow) => {
    setCurrentFlow(flow);
    // Sync nodes/connections as handleEditFlow does
    const syncNodes = (flow.nodes || []).map(node => {
      if (node.type === 'message_received' && !node.config?.triggerMessage && flow.triggerMessage) {
        return {
          ...node,
          config: { ...(node.config || {}), triggerMessage: flow.triggerMessage }
        };
      }
      return node;
    });

    setFlowData({
      nodes: syncNodes,
      connections: flow.connections || []
    });
    
    setShowFlowBuilder(true);
    fetchFlowAnalytics(flow._id || flow.id);
  };

  const handleCreateFlow = () => {
    const triggerNode = {
      id: `node_${Date.now()}`,
      type: 'message_received',
      x: 100,
      y: 150,
      config: { triggerMessage: '' }
    };

    setCurrentFlow({
      id: null,
      name: '',
      description: '',
      whatsappNumber: user?.whatsappNumber || businessNumbers[0] || '',
      triggerMessage: '',
      trigger: 'message_received',
      nodes: [triggerNode],
      connections: []
    });
    setFlowData({ nodes: [triggerNode], connections: [] });
    setShowFlowBuilder(true);
    handleNodeClick(triggerNode);
  };

  const toggleFunnelFilter = (nodeId, fromPort) => {
    if (!isAnalyticsMode) return;
    
    setActiveFunnelFilters(prev => {
      const exists = prev.find(f => f.nodeId === nodeId && f.fromPort === fromPort);
      let newFilters;
      if (exists) {
        newFilters = prev.filter(f => !(f.nodeId === nodeId && f.fromPort === fromPort));
      } else {
        newFilters = [...prev, { nodeId, fromPort }];
      }
      
      // Fetch fresh filtered data
      fetchFlowAnalytics(currentFlow._id || currentFlow.id, newFilters);
      return newFilters;
    });
  };

  const handleEditFlow = (flow) => {
    setCurrentFlow(flow);

    // Ensure trigger node has the triggerMessage from flow root if missing in node config
    const syncNodes = (flow.nodes || []).map(node => {
      if (node.type === 'message_received' && !node.config?.triggerMessage && flow.triggerMessage) {
        return {
          ...node,
          config: {
            ...(node.config || {}),
            triggerMessage: flow.triggerMessage
          }
        };
      }
      return node;
    });

    setFlowData({
      nodes: syncNodes,
      connections: flow.connections || []
    });
    setShowFlowBuilder(true);
  };

  const handleToggleFlow = async (flowId, newStatus) => {
    try {
      await requestHelpers.put(`/flows/${flowId}`, { status: newStatus });
      setFlows(prev => prev.map(flow =>
        flow._id === flowId ? { ...flow, status: newStatus } : flow
      ));
      toast.success(`Flow ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update flow status');
    }
  };

  const handleDeleteFlow = async (flowId) => {
    if (window.confirm('Are you sure you want to delete this flow?')) {
      try {
        await requestHelpers.delete(`/flows/${flowId}`);
        setFlows(prev => prev.filter(flow => flow._id !== flowId));
        toast.success('Flow deleted successfully');
      } catch (error) {
        toast.error('Failed to delete flow');
      }
    }
  };

  // Handle global mouse move for panning and dragging
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (!canvasRef.current || (!isPanning && !isMovingNode && !pendingConnection)) return;

      const rect = canvasRef.current.getBoundingClientRect();
      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;

      if (pendingConnection) {
        setMousePos({
          x: currentX - canvasOffset.x,
          y: currentY - canvasOffset.y
        });
      }

      if (isPanning) {
        setCanvasOffset({
          x: e.clientX - panStart.x,
          y: e.clientY - panStart.y
        });
      }

      if (isMovingNode && movingNodeId) {
        const workspaceX = currentX - canvasOffset.x;
        const workspaceY = currentY - canvasOffset.y;

        setFlowData(prev => ({
          ...prev,
          nodes: prev.nodes.map(n =>
            n.id === movingNodeId
              ? { ...n, x: workspaceX - dragOffset.x, y: workspaceY - dragOffset.y }
              : n
          )
        }));
      }
    };

    const handleGlobalMouseUp = () => {
      setIsMovingNode(false);
      setMovingNodeId(null);
      setIsPanning(false);

      // Deferred reset for pending connections to allow port onMouseUp to trigger first
      setTimeout(() => {
        setPendingConnection(null);
      }, 0);
    };

    if (isPanning || isMovingNode || pendingConnection) {
      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isPanning, isMovingNode, movingNodeId, panStart, dragOffset, canvasOffset, pendingConnection]);

  // Flow Builder Functions
  const handleDragStart = (e, nodeType) => {
    setDraggedNode(nodeType);
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (!draggedNode || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - canvasOffset.x;
    const y = e.clientY - rect.top - canvasOffset.y;

    const newNode = {
      id: `node_${Date.now()}`,
      type: draggedNode.id,
      x: x - 110,
      y: y - 22,
      config: {}
    };

    setFlowData(prev => ({
      ...prev,
      nodes: [...prev.nodes, newNode]
    }));

    setDraggedNode(null);
    setIsDragging(false);
  };

  const handleCanvasMouseDown = (e) => {
    if (e.target.classList.contains('flow-canvas') || e.target.closest('.connections-layer')) {
      setIsPanning(true);
      setPanStart({
        x: e.clientX - canvasOffset.x,
        y: e.clientY - canvasOffset.y
      });
    }
  };

  const handleNodeMouseDown = (e, node) => {
    if (e.target.closest('.input-port') || e.target.closest('.output-port') || e.target.closest('.node-delete-btn') || e.target.closest('.node-button-pill')) {
      return;
    }

    e.stopPropagation();
    setIsMovingNode(true);
    setMovingNodeId(node.id);
    setSelectedNode(node);

    const rect = canvasRef.current.getBoundingClientRect();
    const workspaceX = e.clientX - rect.left - canvasOffset.x;
    const workspaceY = e.clientY - rect.top - canvasOffset.y;

    setDragOffset({
      x: workspaceX - node.x,
      y: workspaceY - node.y
    });
  };

  const handleDeleteNode = (e, nodeId) => {
    e.stopPropagation();
    if (window.confirm('Delete this node and all its connections?')) {
      setFlowData(prev => ({
        ...prev,
        nodes: prev.nodes.filter(n => n.id !== nodeId),
        connections: prev.connections.filter(c => c.from !== nodeId && c.to !== nodeId)
      }));
      if (selectedNode?.id === nodeId) {
        setSelectedNode(null);
      }
    }
  };

  const handleNodeClick = (node) => {
    setSelectedNode(node);
  };

  const handleNodeConfigChange = (nodeId, config) => {
    setFlowData(prev => {
      const updatedNodes = prev.nodes.map(node =>
        node.id === nodeId ? { ...node, config } : node
      );

      // Keep selectedNode in sync with flowData
      if (selectedNode?.id === nodeId) {
        setSelectedNode(prevSelected => ({ ...prevSelected, config }));
      }

      return { ...prev, nodes: updatedNodes };
    });
  };

  const deleteConnection = (index) => {
    setFlowData(prev => ({
      ...prev,
      connections: prev.connections.filter((_, i) => i !== index)
    }));
  };

  const handleMediaUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    // Meta-compliant validation
    const isImage = type === 'image' || file.type.startsWith('image/');
    const isVideo = type === 'video' || file.type.startsWith('video/');
    const isDocument = type === 'document' || file.type === 'application/pdf' || file.name.endsWith('.pdf') || file.name.endsWith('.doc') || file.name.endsWith('.docx');

    let sizeLimit = 16 * 1024 * 1024; // Default (Video/Audio)
    let sizeLimitLabel = '16MB';

    if (isImage) {
      sizeLimit = 5 * 1024 * 1024;
      sizeLimitLabel = '5MB';
    } else if (isDocument) {
      sizeLimit = 100 * 1024 * 1024;
      sizeLimitLabel = '100MB';
    }

    if (file.size > sizeLimit) {
      toast.error(`${type.charAt(0).toUpperCase() + type.slice(1)} size exceeds Meta restriction (Max ${sizeLimitLabel}).`);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploadingMedia(true);
    try {
      const response = await requestHelpers.post('/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.success) {
        handleNodeConfigChange(selectedNode.id, {
          ...selectedNode.config,
          url: response.data.url,
          mediaSource: 'upload',   // Mark as uploaded so UI restores correctly on re-edit
          mediaFilename: file.name // Store filename for display
        });
        toast.success('Media uploaded successfully');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to upload media');
    } finally {
      setUploadingMedia(false);
    }
  };

  const validateFlowData = () => {
    if (!flowData?.nodes || flowData.nodes.length === 0) {
      toast.error('Flow must contain at least one node');
      return false;
    }

    // 1. Presence of a trigger node
    const hasTrigger = flowData.nodes.some(n => 
      n.type === 'message_received' || 
      n.type === 'webhook_received' ||
      (n.type === 'send_template' && !flowData.connections.some(c => c.to === n.id))
    );
    if (!hasTrigger) {
      toast.error('Flow must have a trigger node (Message Received, Webhook, or Send Template as root)');
      return false;
    }

    // 2. Validate each node's required config fields
    for (const node of flowData.nodes) {
      const type = node.type;
      const config = node.config || {};
      const nodeName = node.name || type;

      if (type === 'send_message') {
        if (!config.message?.trim()) {
          toast.error(`"${nodeName}" node: Message content is required`);
          return false;
        }
        if (config.type && config.type !== 'text' && !config.mediaId) {
          toast.error(`"${nodeName}" node: Please select a media file from the library`);
          return false;
        }
      }

      if (type === 'send_template') {
        if (!config.templateId) {
          toast.error(`"${nodeName}" node: Please select a template`);
          return false;
        }
        if (config.hasMediaHeader && !config.mediaId) {
          toast.error(`"${nodeName}" node: Please select a header media file from the library`);
          return false;
        }
      }

      if (type === 'list_messages') {
        if (!config.message?.trim()) {
          toast.error(`"${nodeName}" node: Body message content is required`);
          return false;
        }
        if (!config.buttonLabel?.trim()) {
          toast.error(`"${nodeName}" node: Menu Button Label is required`);
          return false;
        }
        if (!config.rows || config.rows.length === 0) {
          toast.error(`"${nodeName}" node: At least one list row is required`);
          return false;
        }
        if (config.rows.some(r => !r.title?.trim())) {
          toast.error(`"${nodeName}" node: All list rows must have a title`);
          return false;
        }
      }

      if (type === 'wait_delay') {
        if (config.delay === undefined || config.delay === null || Number.isNaN(Number(config.delay)) || Number(config.delay) <= 0) {
          toast.error(`"${nodeName}" node: Wait duration must be a valid number greater than 0`);
          return false;
        }
      }

      if (type === 'add_tag' || type === 'remove_tag' || type === 'contact_has_tag') {
        if (!config.tag?.trim()) {
          toast.error(`"${nodeName}" node: Tag name is required`);
          return false;
        }
      }

      if (type === 'update_contact') {
        if (!config.fieldName) {
          toast.error(`"${nodeName}" node: Field name is required`);
          return false;
        }
        if (!config.fieldValue?.trim()) {
          toast.error(`"${nodeName}" node: New value is required`);
          return false;
        }
      }

      if (type === 'if_condition') {
        if (!config.condition?.trim()) {
          toast.error(`"${nodeName}" node: Condition logic is required`);
          return false;
        }
      }

      if (type === 'message_contains') {
        if (!config.condition?.trim()) {
          toast.error(`"${nodeName}" node: Message keyword condition is required`);
          return false;
        }
      }

      if (type === 'api_request') {
        if (!config.apiEndpoint?.trim()) {
          toast.error(`"${nodeName}" node: Endpoint URL is required`);
          return false;
        }
      }

      if (type === 'interactive_buttons') {
        if (!config.message?.trim()) {
          toast.error(`"${nodeName}" node: Body message content is required`);
          return false;
        }
        if (!config.buttons || config.buttons.length === 0) {
          toast.error(`"${nodeName}" node: At least one button is required`);
          return false;
        }
        if (config.buttons.some(b => !b.text?.trim())) {
          toast.error(`"${nodeName}" node: All buttons must have a label`);
          return false;
        }
        for (let i = 0; i < config.buttons.length; i++) {
          const btn = config.buttons[i];
          if (btn.type === 'url' && !btn.url?.trim()) {
            toast.error(`"${nodeName}" node: Button "${btn.text}" is missing a website URL`);
            return false;
          }
          if (btn.type === 'call' && !btn.phoneNumber?.trim()) {
            toast.error(`"${nodeName}" node: Button "${btn.text}" is missing a phone number`);
            return false;
          }
        }
      }
    }

    return true;
  };

  const handleSaveFlow = async () => {
    if (!currentFlow?.name) {
      toast.error('Please enter a flow name');
      return;
    }

    if (!validateFlowData()) {
      return;
    }

    try {
      // Find the message_received node to sync triggerMessage to the main flow object
      const triggerNode = flowData.nodes.find(n => n.type === 'message_received');

      const flowToSave = {
        ...currentFlow,
        triggerMessage: triggerNode?.config?.triggerMessage || currentFlow.triggerMessage || '',
        nodes: flowData.nodes,
        connections: flowData.connections
      };

      if (currentFlow.id || currentFlow._id) {
        const id = currentFlow._id || currentFlow.id;
        await requestHelpers.put(`/flows/${id}`, flowToSave);
        toast.success('Flow updated successfully');
      } else {
        await requestHelpers.post('/flows', flowToSave);
        toast.success('Flow created successfully');
      }

      setShowFlowBuilder(false);
      fetchFlows();
      setCurrentFlow(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save flow');
    }
  };

  const getPortPosition = (node, portIndex, isOutput) => {
    const nodeWidth = 260; // Rev G
    const nodeHeaderHeight = 44;
    const messageBoxHeight = 50;
    const buttonRowHeight = 42;
    const buttonListPadding = 12;
    const portOffset = 10;
    const portRadius = 7;

    if (!isOutput) {
      return {
        x: node.x + portOffset + portRadius,
        y: node.y + (nodeHeaderHeight / 2) // Centered in Header
      };
    }

    let yOffset = nodeHeaderHeight;

    if (node.config.header) yOffset += 24;

    const hasMessage = node.config.message || node.config.triggerMessage || node.type === 'message_received';
    if (hasMessage) {
      yOffset += messageBoxHeight;
    }

    if (node.config.footer) yOffset += 20;

    if (node.type === 'interactive_buttons' || (node.type === 'send_template' && node.config.buttons?.length > 0)) {
      yOffset += buttonListPadding;
      yOffset += (portIndex * buttonRowHeight) + (buttonRowHeight / 2);
    } else if (node.type === 'list_messages') {
      yOffset += buttonListPadding;
      // List row titles take same space as button pils
      yOffset += (portIndex * buttonRowHeight) + (buttonRowHeight / 2);
    } else {
      yOffset += 20;
    }

    return {
      x: node.x + nodeWidth - portOffset - portRadius,
      y: node.y + yOffset
    };
  };

  const getNodeTemplate = (nodeType) => {
    for (const category of nodeTypes) {
      const node = category.items.find(item => item.id === nodeType);
      if (node) return node;
    }
    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    return status === 'active' ? 'badge-success' : 'badge-secondary';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <LoadingSpinner />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="automation-page">
        {!showFlowBuilder ? (
          <>
            <div className="page-header">
              <div className="header-content">
                <h1>Automation</h1>
                <p>Create automated workflows to streamline your messaging</p>
                {user?.whatsappNumber && (
                  <div className="header-number-badge mt-2" style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' }}>
                    <i className="fab fa-whatsapp"></i>
                    <span>Linked to: {user.whatsappNumber}</span>
                  </div>
                )}
              </div>
              <div className="header-actions">
                <button className="btn btn-secondary">
                  <i className="fas fa-download"></i>
                  Export Flows
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleCreateFlow}
                >
                  <i className="fas fa-plus"></i>
                  Create Flow
                </button>
              </div>
            </div>

            <div className="flows-grid">
              {flows.map(flow => (
                <div key={flow.id} className="flow-card">
                  <div className="flow-header">
                    <div className="flow-title">
                      <h3>{flow.name}</h3>
                      <span className={`status-badge ${getStatusBadge(flow.status)}`}>
                        {flow.status}
                      </span>
                    </div>
                    <div className="flow-actions">
                      <button
                        className="action-btn"
                        onClick={() => handleEditFlow(flow)}
                        title="Edit Flow"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="action-btn analytics-btn"
                        onClick={() => handleAnalyzeFlow(flow)}
                        title="Analyze Interactions"
                      >
                        <i className="fas fa-chart-line"></i>
                      </button>
                      <button
                        className={`action-btn ${flow.status === 'active' ? 'active' : ''}`}
                        onClick={() => handleToggleFlow(flow.id, flow.status === 'active' ? 'inactive' : 'active')}
                        title={flow.status === 'active' ? 'Deactivate' : 'Activate'}
                      >
                        <i className={`fas ${flow.status === 'active' ? 'fa-pause' : 'fa-play'}`}></i>
                      </button>
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDeleteFlow(flow.id)}
                        title="Delete Flow"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>

                  {flow.description && (
                    <div className="flow-description">
                      <p>{flow.description}</p>
                    </div>
                  )}

                  <div className="flow-stats">
                    <div className="stat-item">
                      <span className="stat-label">
                        <i className="fas fa-bolt mr-1"></i>
                        Trigger
                      </span>
                      <span className="stat-value">{flow.trigger.replace('_', ' ')}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">
                        <i className="fas fa-play-circle mr-1"></i>
                        Executions
                      </span>
                      <span className="stat-value">{flow.statistics?.totalExecutions || flow.executions || 0}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">
                        <i className="fas fa-history mr-1"></i>
                        Last Run
                      </span>
                      <span className="stat-value">{formatDate(flow.lastExecution?.date || flow.lastRun)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {flows.length === 0 && (
              <div className="empty-state">
                <i className="fas fa-robot"></i>
                <h3>No automation flows found</h3>
                <p>Create your first automation flow to streamline your messaging</p>
                <button
                  className="btn btn-primary"
                  onClick={handleCreateFlow}
                >
                  Create Your First Flow
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flow-builder">
            <div className="flow-builder-header">
              <div className="builder-title">
                <div className="builder-main-info">
                  <input
                    type="text"
                    placeholder="Enter flow name..."
                    value={currentFlow?.name || ''}
                    onChange={(e) => setCurrentFlow(prev => ({ ...prev, name: e.target.value }))}
                    className="flow-name-input"
                  />
                  <input
                    type="text"
                    placeholder="Flow description..."
                    value={currentFlow?.description || ''}
                    onChange={(e) => setCurrentFlow(prev => ({ ...prev, description: e.target.value }))}
                    className="flow-description-input"
                  />
                </div>
                <div className="builder-config-info">
                  <div className="config-item">
                    <label>WhatsApp Number</label>
                    <select
                      value={currentFlow?.whatsappNumber || ''}
                      onChange={(e) => setCurrentFlow(prev => ({ ...prev, whatsappNumber: e.target.value }))}
                      className="flow-select-input"
                    >
                      <option value="">Select a number...</option>
                      {businessNumbers.map(num => (
                        <option key={num} value={num}>{num}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="builder-actions">
                {currentFlow && (
                  <button 
                    className={`btn ${isAnalyticsMode ? 'btn-success' : 'btn-info'}`}
                    style={{ background: isAnalyticsMode ? '#10b981' : '#3b82f6', color: 'white' }}
                    onClick={() => isAnalyticsMode ? setIsAnalyticsMode(false) : fetchFlowAnalytics(currentFlow._id)}
                    disabled={loadingAnalytics}
                  >
                    {loadingAnalytics ? <i className="fas fa-spinner fa-spin mr-1"></i> : <i className="fas fa-chart-line mr-1"></i>}
                    {isAnalyticsMode ? 'Hide Stats' : 'Analyze Interactions'}
                  </button>
                )}
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowFlowBuilder(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveFlow}
                >
                  Save Flow
                </button>
              </div>
            </div>

            <div className="flow-builder-content">
              <div className="node-palette">
                <h3>Flow Components</h3>
                {nodeTypes.map(category => (
                  <div key={category.type} className="node-category">
                    <h4>{category.category}</h4>
                    <div className="node-items">
                      {category.items.map(node => (
                        <div
                          key={node.id}
                          className="palette-node"
                          draggable
                          onDragStart={(e) => handleDragStart(e, node)}
                        >
                          <i className={node.icon}></i>
                          <span>{node.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flow-canvas-container">
                <div
                  className={`flow-canvas ${isPanning ? 'panning' : ''}`}
                  ref={canvasRef}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onMouseDown={handleCanvasMouseDown}
                >
                  <div
                    className="flow-workspace"
                    style={{
                      transform: `translate(${canvasOffset.x}px, ${canvasOffset.y}px)`,
                    }}
                  >
                    <svg className="connections-layer">
                      {flowData.connections.map((conn, idx) => {
                        const fromNode = flowData.nodes.find(n => n.id === conn.from);
                        const toNode = flowData.nodes.find(n => n.id === conn.to);
                        if (!fromNode || !toNode) return null;

                        const startPos = getPortPosition(fromNode, conn.fromPort || 0, true);
                        const endPos = getPortPosition(toNode, conn.toPort || 0, false);
                        
                        const isFilterActive = activeFunnelFilters.some(
                          f => f.nodeId === conn.from && Number(f.fromPort) === Number(conn.fromPort)
                        );

                        return (
                          <g key={idx} className={`connection-group ${isAnalyticsMode ? 'analytic-mode' : ''} ${isFilterActive ? 'active-filter' : ''}`}>
                            {/* Invisible wide line for easier clicking */}
                            <line
                              x1={startPos.x + 7}
                              y1={startPos.y}
                              x2={endPos.x - 10}
                              y2={endPos.y}
                              className="connection-hit-area"
                              onClick={(e) => {
                                if (isAnalyticsMode) {
                                  toggleFunnelFilter(conn.from, conn.fromPort);
                                }
                              }}
                            />
                            <line
                              x1={startPos.x + 7}
                              y1={startPos.y}
                              x2={endPos.x - 10}
                              y2={endPos.y}
                              className={`connection-line ${isFilterActive ? 'active' : ''}`}
                              markerEnd="url(#arrowhead)"
                            />
                            {!isAnalyticsMode && (
                              <g 
                                className="connection-delete-trigger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteConnection(idx);
                                }}
                              >
                                <circle 
                                  cx={(startPos.x + endPos.x) / 2} 
                                  cy={(startPos.y + endPos.y) / 2} 
                                  r="10" 
                                  fill="#ef4444" 
                                  className="delete-circle"
                                />
                                <text 
                                  x={(startPos.x + endPos.x) / 2} 
                                  y={(startPos.y + endPos.y) / 2 + 4} 
                                  textAnchor="middle" 
                                  fill="white" 
                                  fontSize="12" 
                                  fontWeight="bold"
                                  style={{ pointerEvents: 'none' }}
                                >×</text>
                              </g>
                            )}
                          </g>
                        );
                      })}
                      {pendingConnection && (() => {
                        const fromNode = flowData.nodes.find(n => n.id === pendingConnection.nodeId);
                        if (!fromNode) return null;
                        const startPos = getPortPosition(fromNode, pendingConnection.portIndex, true);

                        return (
                          <line
                            x1={startPos.x + 7}
                            y1={startPos.y}
                            x2={mousePos.x}
                            y2={mousePos.y}
                            className="connection-line pending"
                          />
                        );
                      })()}
                      <defs>
                        <marker id="arrowhead" markerWidth="10" markerHeight="7"
                          refX="10" refY="3.5" orient="auto">
                          <polygon points="0 0, 10 3.5, 0 7" fill="#94a3b8" />
                        </marker>
                      </defs>
                    </svg>

                    {flowData.nodes.map(node => {
                      const template = getNodeTemplate(node.type);
                      return (
                        <div
                          key={node.id}
                          className={`flow-node node-${node.type} ${selectedNode?.id === node.id ? 'selected' : ''}`}
                          style={{ left: node.x, top: node.y }}
                          onMouseDown={(e) => handleNodeMouseDown(e, node)}
                        >
                          {isAnalyticsMode && analyticsData && (
                            <div className="node-analytics-badge anim-pulse">
                              <i className="fas fa-users mr-1"></i> {analyticsData.dropOffData[node.id] || 0} hits
                            </div>
                          )}
                          <div className="node-header">
                            {template?.inputs > 0 && (
                              <div
                                className="input-port"
                                title="Drop here to connect"
                                onMouseUp={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  if (pendingConnection && pendingConnection.nodeId !== node.id) {
                                    setFlowData(prev => ({
                                      ...prev,
                                      connections: [
                                        ...prev.connections,
                                        {
                                          from: pendingConnection.nodeId,
                                          to: node.id,
                                          fromPort: pendingConnection.portIndex,
                                          toPort: 0
                                        }
                                      ]
                                    }));
                                    setPendingConnection(null);
                                  }
                                }}
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                              ></div>
                            )}
                            <div className="header-icon-name">
                              <i className={template?.icon}></i>
                              <span>{template?.name}</span>
                            </div>
                            <button
                              className="node-delete-btn"
                              onClick={(e) => handleDeleteNode(e, node.id)}
                              title="Delete Node"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>

                          <div className="node-content-box">
                            {node.config.header && (
                              <div className="node-message-header">{node.config.header}</div>
                            )}
                            <div className="message-text">
                              {node.type === 'wait_delay' ? (
                                `Wait for ${node.config.delay || 1} ${node.config.delayUnit || node.config.unit || 'minutes'}`
                              ) : (
                                node.config.message || node.config.triggerMessage || (node.type === 'message_received' ? 'Triggers on incoming message' : '')
                              )}
                            </div>
                            {node.config.footer && (
                              <div className="node-message-footer">{node.config.footer}</div>
                            )}
                          </div>

                          <div className="node-body">

                            {node.type === 'list_messages' && (
                              <div className="node-list-rows">
                                <div className="node-list-menu-trigger">
                                  <i className="fas fa-bars"></i>
                                  <span>{node.config.buttonLabel || 'Select Option'}</span>
                                </div>
                                <div className="node-list-divider">Options</div>
                                {(node.config.rows || []).slice(0, 3).map((row, idx) => (
                                  <div
                                    key={idx}
                                    className="node-list-row"
                                    onMouseDown={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      setPendingConnection({ nodeId: node.id, portIndex: idx });
                                    }}
                                  >
                                    <div className="list-row-content">
                                      <span className="row-title">{row.title || 'New Row'}</span>
                                      {row.description && <span className="row-desc">{row.description}</span>}
                                    </div>
                                    <div className="output-port"></div>
                                  </div>
                                ))}
                                {node.config.rows?.length > 3 && (
                                  <div className="node-list-more">+ {node.config.rows.length - 3} more options...</div>
                                )}
                                {(!node.config.rows || node.config.rows.length === 0) && (
                                  <div className="node-placeholder-text">Add rows in config...</div>
                                )}
                              </div>
                            )}

                            {node.type === 'interactive_buttons' && (
                              <div className="node-buttons-list">
                                {(node.config.buttons || []).map((btn, idx) => (
                                  <div
                                    key={idx}
                                    className={`node-button-pill ${btn.type || 'reply'}`}
                                    onMouseDown={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      setPendingConnection({ nodeId: node.id, portIndex: idx });
                                    }}
                                  >
                                    <div className="button-pill-content">
                                      {btn.type === 'url' && <i className="fas fa-external-link-alt"></i>}
                                      {btn.type === 'call' && <i className="fas fa-phone"></i>}
                                      <span>{btn.text || 'New Button'}</span>
                                    </div>
                                    <div className="output-port"></div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {node.type === 'send_template' && node.config.buttons?.length > 0 && (
                              <div className="node-buttons-list">
                                {node.config.buttons.map((btn, idx) => (
                                  <div
                                    key={idx}
                                    className="node-button-pill"
                                    onMouseDown={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      setPendingConnection({ nodeId: node.id, portIndex: idx });
                                    }}
                                  >
                                    <div className="button-pill-content">
                                      <span>{btn.text || 'Template Button'}</span>
                                    </div>
                                    <div className="output-port"></div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {(node.type !== 'interactive_buttons' && (node.type !== 'send_template' || !node.config.buttons?.length)) && template?.outputs > 0 && (
                              <div
                                className="output-port-default"
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setPendingConnection({ nodeId: node.id, portIndex: 0 });
                                }}
                              >
                                <div className="output-port"></div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {selectedNode && (
                <div className="node-config-panel">
                  <h3>Configure Node</h3>
                  <div className="config-form">
                    {/* {selectedNode.type === 'send_message' && (
                      <div className="form-group">
                        <label>Message</label>
                        <textarea
                          value={selectedNode.config.message || ''}
                          onChange={(e) => handleNodeConfigChange(selectedNode.id, { 
                            ...selectedNode.config, 
                            message: e.target.value 
                          })}
                          placeholder="Enter message to send..."
                          rows="3"
                        />
                      </div>
                    )} */}

                    {selectedNode.type === 'send_message' && (
                      <div className="config-form-section">
                        <div className="form-group">
                          <label>Header (Optional) <span className="char-count">{(selectedNode.config.header || '').length}/60</span></label>
                          <input
                            type="text"
                            value={selectedNode.config.header || ''}
                            onChange={(e) => handleNodeConfigChange(selectedNode.id, {
                              ...selectedNode.config,
                              header: e.target.value
                            })}
                            placeholder="Bold header text..."
                            maxLength="60"
                          />
                        </div>

                        <div className="form-group">
                          <label>Message Content (Body) <span style={{ color: 'red' }}>*</span> <span className="char-count">{(selectedNode.config.message || '').length}/1024</span></label>
                          <textarea
                            value={selectedNode.config.message || ''}
                            onChange={(e) =>
                              handleNodeConfigChange(selectedNode.id, {
                                ...selectedNode.config,
                                message: e.target.value,
                              })
                            }
                            placeholder="Enter message to send..."
                            rows="4"
                            maxLength="1024"
                          />
                          <div className="char-count-container">
                            {/* <small className="variable-hint">Tip: Use <code>{"{{name}}"}</code> to personalize</small> */}
                            <div className={`char-counter ${(selectedNode.config.message?.length || 0) > 900 ? 'warning' : ''}`}>
                              {(selectedNode.config.message?.length || 0)}/1024
                            </div>
                          </div>
                        </div>

                        <div className="form-group">
                          <label>Footer (Optional) <span className="char-count">{(selectedNode.config.footer || '').length}/60</span></label>
                          <input
                            type="text"
                            value={selectedNode.config.footer || ''}
                            onChange={(e) => handleNodeConfigChange(selectedNode.id, {
                              ...selectedNode.config,
                              footer: e.target.value
                            })}
                            placeholder="Small muted text at bottom..."
                            maxLength="60"
                          />
                        </div>

                        <div className="form-group">
                          <label>Media Attachment (Optional)</label>
                          <select
                            value={selectedNode.config.type || 'text'}
                            onChange={(e) =>
                              handleNodeConfigChange(selectedNode.id, {
                                ...selectedNode.config,
                                type: e.target.value,
                                mediaId: '',
                                url: '',
                                mediaFilename: ''
                              })
                            }
                          >
                            <option value="text">None (Text Only)</option>
                            <option value="image">Image</option>
                            <option value="video">Video</option>
                            <option value="audio">Audio</option>
                             <option value="document">Document (PDF / Doc)</option>
                          </select>
                        </div>

                        {selectedNode.config.type && selectedNode.config.type !== 'text' && (
                          <div className="form-group mt-2">
                            <label>Select Media File <span style={{ color: 'red' }}>*</span></label>
                            <select
                              value={selectedNode.config.mediaId || ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                const media = availableMedia.find(m => m.mediaId === val);
                                handleNodeConfigChange(selectedNode.id, {
                                  ...selectedNode.config,
                                  mediaId: val,
                                  url: val,
                                  mediaFilename: media?.fileName || ''
                                });
                              }}
                              required
                            >
                              <option value="">Select from Media Library...</option>
                              {availableMedia
                                .filter(m => m.fileType === selectedNode.config.type)
                                .map(media => (
                                  <option key={media.mediaId} value={media.mediaId}>
                                    {media.fileName} ({media.daysRemaining} days left)
                                  </option>
                                ))
                              }
                            </select>
                            {availableMedia.filter(m => m.fileType === selectedNode.config.type).length === 0 && (
                              <div className="alert alert-warning mt-2 p-2" style={{ fontSize: '11px' }}>
                                <i className="fas fa-exclamation-triangle"></i> No {selectedNode.config.type}s found in library. 
                                <a href="/media-library" target="_blank" className="ml-1 font-weight-bold">Upload here</a>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {selectedNode.type === 'list_messages' && (
                      <div className="config-form-section">
                        <div className="form-group">
                          <label>Header (Optional) <span className="char-count">{(selectedNode.config.header || '').length}/60</span></label>
                          <input
                            type="text"
                            value={selectedNode.config.header || ''}
                            onChange={(e) => handleNodeConfigChange(selectedNode.id, {
                              ...selectedNode.config,
                              header: e.target.value
                            })}
                            placeholder="Bold header text..."
                            maxLength="60"
                          />
                        </div>
                        <div className="form-group">
                          <label>Body <span style={{ color: 'red' }}>*</span> <span className="char-count">{(selectedNode.config.message || '').length}/1024</span></label>
                          <textarea
                            value={selectedNode.config.message || ''}
                            onChange={(e) => handleNodeConfigChange(selectedNode.id, {
                              ...selectedNode.config,
                              message: e.target.value
                            })}
                            placeholder="Main message text..."
                            rows="4"
                            maxLength="1024"
                          />
                        </div>
                        <div className="form-group">
                          <label>Footer (Optional) <span className="char-count">{(selectedNode.config.footer || '').length}/60</span></label>
                          <input
                            type="text"
                            value={selectedNode.config.footer || ''}
                            onChange={(e) => handleNodeConfigChange(selectedNode.id, {
                              ...selectedNode.config,
                              footer: e.target.value
                            })}
                            placeholder="Small muted text..."
                            maxLength="60"
                          />
                        </div>
                        <div className="form-group">
                          <label>Menu Button Label <span style={{ color: 'red' }}>*</span> <span className="char-count">{(selectedNode.config.buttonLabel || '').length}/20</span></label>
                          <input
                            type="text"
                            value={selectedNode.config.buttonLabel || ''}
                            onChange={(e) => handleNodeConfigChange(selectedNode.id, {
                              ...selectedNode.config,
                              buttonLabel: e.target.value
                            })}
                            placeholder="e.g. Choose Option"
                            maxLength="20"
                          />
                        </div>

                        <div className="form-group">
                          <label>List Rows (Max 10)</label>
                          <div className="button-list-config">
                            {(selectedNode.config.rows || []).map((row, idx) => (
                              <div key={idx} className="button-config-item-expanded">
                                <div className="form-group compact">
                                  <label>Title <span style={{ color: 'red' }}>*</span> <span className="char-count">{(row.title || '').length}/24</span></label>
                                  <input
                                    type="text"
                                    value={row.title || ''}
                                    onChange={(e) => {
                                      const newRows = [...selectedNode.config.rows];
                                      newRows[idx].title = e.target.value;
                                      handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, rows: newRows });
                                    }}
                                    placeholder="Row title..."
                                    maxLength="24"
                                  />
                                </div>
                                <div className="form-group compact">
                                  <label>Description <span className="char-count">{(row.description || '').length}/72</span></label>
                                  <input
                                    type="text"
                                    value={row.description || ''}
                                    onChange={(e) => {
                                      const newRows = [...selectedNode.config.rows];
                                      newRows[idx].description = e.target.value;
                                      handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, rows: newRows });
                                    }}
                                    placeholder="Row description (optional)..."
                                    maxLength="72"
                                  />
                                </div>
                                <button
                                  className="remove-btn icon-only"
                                  onClick={() => {
                                    const newRows = selectedNode.config.rows.filter((_, i) => i !== idx);
                                    handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, rows: newRows });
                                  }}
                                  title="Remove Row"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            ))}
                            {(selectedNode.config.rows || []).length < 10 && (
                              <button
                                className="add-button-btn"
                                onClick={() => {
                                  const newRows = [...(selectedNode.config.rows || []), { title: '', description: '' }];
                                  handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, rows: newRows });
                                }}
                              >
                                <i className="fas fa-plus"></i> Add Row
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedNode.type === 'send_flow' && (
                    <div className="config-section">
                      <div className="form-group">
                        <label>Meta Flow *</label>
                        <select
                          value={selectedNode.config.flowId || ''}
                          onChange={(e) => {
                            const flowId = e.target.value;
                            handleNodeConfigChange(selectedNode.id, {
                              ...selectedNode.config,
                              flowId
                            });
                          }}
                          className="config-input"
                          required
                        >
                          <option value="">Select a Flow...</option>
                          {whatsappFlows.map(f => (
                            <option key={f.flowId} value={f.flowId}>{f.name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Header Text (Optional)</label>
                        <input
                          type="text"
                          value={selectedNode.config.header || ''}
                          onChange={(e) => handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, header: e.target.value })}
                          placeholder="e.g. Appointment Request"
                          className="config-input"
                        />
                      </div>

                      <div className="form-group">
                        <label>Message Body *</label>
                        <textarea
                          value={selectedNode.config.message || ''}
                          onChange={(e) => handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, message: e.target.value })}
                          placeholder="Please interact with the form below..."
                          className="config-input"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Button Text (CTA) *</label>
                        <input
                          type="text"
                          value={selectedNode.config.flowCTA || ''}
                          onChange={(e) => handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, flowCTA: e.target.value })}
                          placeholder="e.g. Book Now"
                          className="config-input"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Initial Screen ID *</label>
                        <input
                          type="text"
                          value={selectedNode.config.flowScreen || ''}
                          onChange={(e) => handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, flowScreen: e.target.value })}
                          placeholder="e.g. SCREEN_1"
                          className="config-input"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Dynamic Data (JSON Payload)</label>
                        <textarea
                          value={JSON.stringify(selectedNode.config.flowPayload || {}, null, 2)}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, flowPayload: parsed });
                            } catch (err) {
                              // Silent fail on invalid JSON while typing
                            }
                          }}
                          placeholder='{ "user_name": "{{name}}" }'
                          className="config-input code-font"
                          rows="4"
                        />
                        <small className="help-text">Use {"{{variable}}"} for contact fields.</small>
                      </div>
                    </div>
                  )}
                    {selectedNode.type === 'send_template' && (
                      <div className="config-form-section">
                        <div className="form-group">
                          <label>Select Template <span style={{ color: 'red' }}>*</span></label>
                          <select
                            value={selectedNode.config.templateId || ''}
                            onChange={(e) => {
                              const tmpl = availableTemplates.find(t => t._id === e.target.value);
                              if (!tmpl) return;

                              // Advanced parsing of template components
                              let vars = [];
                              let hasMediaHeader = false;
                              let mediaFormat = 'TEXT';
                              let buttons = [];

                              if (tmpl.components) {
                                tmpl.components.forEach(comp => {
                                  if (comp.type === 'HEADER' && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(comp.format)) {
                                    hasMediaHeader = true;
                                    mediaFormat = comp.format.toLowerCase();
                                  }
                                  
                                  // Find vars in Body and Header
                                  if (comp.text) {
                                    const matches = comp.text.match(/\{\{\d+\}\}/g) || [];
                                    vars = [...vars, ...matches.map(v => v.slice(2, -2))];
                                  }

                                  if (comp.type === 'BUTTONS') {
                                    buttons = comp.buttons || [];
                                  }
                                });
                              } else if (tmpl.content) {
                                // Fallback for simple content string
                                const matches = tmpl.content.match(/\{\{\d+\}\}/g) || [];
                                vars = matches.map(v => v.slice(2, -2));
                              }

                              vars = [...new Set(vars)].sort((a, b) => a - b);
                              
                              const initialMappings = {};
                              vars.forEach(v => {
                                initialMappings[v] = { type: 'contact_field', value: 'name' };
                              });

                              handleNodeConfigChange(selectedNode.id, {
                                ...selectedNode.config,
                                templateId: e.target.value,
                                templateName: tmpl?.name || '',
                                variableMappings: initialMappings,
                                hasMediaHeader,
                                mediaFormat,
                                buttons,
                                // Initialize empty media if needed
                                type: hasMediaHeader ? mediaFormat : 'text',
                                url: '',
                                mediaId: '',
                                mediaFilename: '',
                                headerMedia: null
                              });
                            }}
                            required
                          >
                            <option value="">Choose a template...</option>
                            {availableTemplates?.filter(t => t.status === 'APPROVED').map(tmpl => (
                              <option key={tmpl._id} value={tmpl._id}>{tmpl.displayName || tmpl.name}</option>
                            ))}
                          </select>
                        </div>

                        {selectedNode.config.templateId && (
                          <div className="template-extra-config mt-4">
                            
                            {/* Media Header Support */}
                            {selectedNode.config.hasMediaHeader && (
                              <div className="form-group media-config-box">
                                <label><i className="fas fa-photo-video"></i> Template Header {selectedNode.config.mediaFormat?.toUpperCase()} Required <span style={{ color: 'red' }}>*</span></label>
                                
                                <div className="media-selection-dropdown mt-2">
                                  <select
                                    value={selectedNode.config.url || ''}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      const media = availableMedia.find(m => m.mediaId === val);
                                      handleNodeConfigChange(selectedNode.id, { 
                                        ...selectedNode.config, 
                                        url: val,
                                        mediaId: val,
                                        mediaFilename: media?.fileName || '',
                                        headerMedia: val ? { type: selectedNode.config.mediaFormat, id: val } : null
                                      });
                                    }}
                                    required
                                  >
                                    <option value="">Select from Media Library...</option>
                                    {availableMedia
                                      .filter(m => m.fileType === (selectedNode.config.mediaFormat === 'document' ? 'document' : selectedNode.config.mediaFormat))
                                      .map(media => (
                                        <option key={media.mediaId} value={media.mediaId}>
                                          {media.fileName} ({media.daysRemaining} days left)
                                        </option>
                                      ))
                                    }
                                  </select>
                                </div>

                                {availableMedia.filter(m => m.fileType === (selectedNode.config.mediaFormat === 'document' ? 'document' : selectedNode.config.mediaFormat)).length === 0 && (
                                  <div className="alert alert-warning mt-2 p-2" style={{ fontSize: '11px' }}>
                                    <i className="fas fa-exclamation-triangle"></i> No {selectedNode.config.mediaFormat}s found in library. 
                                    <a href="/media-library" target="_blank" className="ml-1 font-weight-bold">Upload here</a>
                                  </div>
                                )}
                                
                                {selectedNode.config.url && (
                                  <small className="d-block mt-2 text-success">
                                    <i className="fas fa-check-circle"></i> Selected: {selectedNode.config.mediaFilename || 'Selected Media'}
                                  </small>
                                )}
                              </div>
                            )}

                            {/* Conditional Variable Mapping */}
                            {Object.keys(selectedNode.config.variableMappings || {}).length > 0 && (
                              <div className="template-variable-mapping">
                                <h4><i className="fas fa-magic"></i> Variable Mapping</h4>
                                {Object.keys(selectedNode.config.variableMappings || {}).map(varNum => (
                                  <div key={varNum} className="mapping-row">
                                    <label>Variable {"{{"}{varNum}{"}}"}</label>
                                    <select 
                                      value={selectedNode.config.variableMappings[varNum]?.value || 'name'}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        const newMappings = { ...selectedNode.config.variableMappings };
                                        newMappings[varNum] = val === 'custom' 
                                          ? { type: 'custom', value: '' }
                                          : { type: 'contact_field', value: val };
                                        handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, variableMappings: newMappings });
                                      }}
                                    >
                                      <option value="name">Contact Name</option>
                                      <option value="firstname">First Name</option>
                                      <option value="email">Email</option>
                                      <option value="phone">Phone Number</option>
                                      <option value="custom">Custom Text</option>
                                    </select>
                                    {selectedNode.config.variableMappings[varNum]?.type === 'custom' && (
                                      <input 
                                        type="text"
                                        placeholder="Enter custom value..."
                                        value={selectedNode.config.variableMappings[varNum].value}
                                        onChange={(e) => {
                                          const newMappings = { ...selectedNode.config.variableMappings };
                                          newMappings[varNum].value = e.target.value;
                                          handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, variableMappings: newMappings });
                                        }}
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Buttons Info */}
                            {selectedNode.config.buttons?.length > 0 && (
                              <div className="template-buttons-info mt-3 p-3 bg-light rounded">
                                <label className="d-block mb-2 font-weight-bold"><i className="fas fa-mouse-pointer"></i> Template Buttons</label>
                                <div className="d-flex flex-wrap gap-2">
                                  {selectedNode.config.buttons.map((btn, i) => (
                                    <span key={i} className="badge badge-info p-2">{btn.text}</span>
                                  ))}
                                </div>
                                <small className="text-muted mt-2 d-block">Connect these buttons to next nodes in the canvas.</small>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {selectedNode.type === 'wait_delay' && (
                      <div className="config-form-section">
                        <div className="form-group">
                          <label>Wait Duration <span style={{ color: 'red' }}>*</span></label>
                          <div className="duration-input-group">
                            <input
                              type="number"
                              min="1"
                              value={selectedNode.config.delay || 1}
                              onChange={(e) => handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, delay: parseInt(e.target.value) })}
                            />
                            <select
                              value={selectedNode.config.delayUnit || 'minutes'}
                              onChange={(e) => handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, delayUnit: e.target.value, unit: e.target.value })}
                            >
                              <option value="seconds">Seconds</option>
                              <option value="minutes">Minutes</option>
                              <option value="hours">Hours</option>
                              <option value="days">Days</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    )}

                    {(selectedNode.type === 'add_tag' || selectedNode.type === 'remove_tag') && (
                      <div className="config-form-section">
                        <div className="form-group">
                          <label>Select Tag <span style={{ color: 'red' }}>*</span></label>
                          <select
                            value={['customer', 'lead', 'vip', 'interested'].includes(selectedNode.config.tag) ? selectedNode.config.tag : (selectedNode.config.tag ? 'custom' : '')}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === 'custom') {
                                handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, tag: '' });
                              } else {
                                handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, tag: val });
                              }
                            }}
                          >
                            <option value="">-- Choose Tag --</option>
                            <option value="customer">Customer</option>
                            <option value="lead">Lead</option>
                            <option value="vip">VIP</option>
                            <option value="interested">Interested</option>
                            <option value="custom">Custom Tag...</option>
                          </select>
                        </div>
                        {(!['customer', 'lead', 'vip', 'interested'].includes(selectedNode.config.tag) || selectedNode.config.tag === 'custom') && (
                          <div className="form-group mt-2">
                            <label>Custom Tag Name <span style={{ color: 'red' }}>*</span></label>
                            <input
                              type="text"
                              placeholder="Type custom tag name..."
                              value={selectedNode.config.tag || ''}
                              onChange={(e) => handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, tag: e.target.value })}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {selectedNode.type === 'add_to_campaign' && (
                      <div className="config-form-section">
                        <div className="form-group">
                          <label>Select Campaign</label>
                          <select
                            value={selectedNode.config.campaignId || ''}
                            onChange={(e) => handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, campaignId: e.target.value })}
                          >
                            <option value="">Choose a campaign...</option>
                            {availableCampaigns.map(c => (
                              <option key={c._id} value={c._id}>{c.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {selectedNode.type === 'update_contact' && (
                      <div className="config-form-section">
                        <div className="form-group">
                          <label>Field Name</label>
                          <select
                            value={selectedNode.config.fieldName || 'name'}
                            onChange={(e) => handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, fieldName: e.target.value })}
                          >
                            <option value="name">Name</option>
                            <option value="email">Email</option>
                            <option value="city">City</option>
                            <option value="company">Company</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>New Value <span style={{ color: 'red' }}>*</span></label>
                          <input
                            type="text"
                            placeholder="New value or {{variable}}"
                            value={selectedNode.config.fieldValue || ''}
                            onChange={(e) => handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, fieldValue: e.target.value })}
                            disabled={selectedNode.config.fieldValue === '{{message}}'}
                          />
                        </div>
                        <div className="form-group mt-2">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="checkbox"
                              id="use-incoming-msg"
                              style={{ width: 'auto', margin: 0 }}
                              checked={selectedNode.config.fieldValue === '{{message}}'}
                              onChange={(e) => {
                                handleNodeConfigChange(selectedNode.id, { 
                                  ...selectedNode.config, 
                                  fieldValue: e.target.checked ? '{{message}}' : '' 
                                });
                              }}
                            />
                            <label htmlFor="use-incoming-msg" style={{ margin: 0, cursor: 'pointer', fontWeight: 500, fontSize: '13px', color: '#475569' }}>
                              Update with the incoming message text
                            </label>
                          </div>
                          <small style={{ display: 'block', color: '#64748b', marginTop: '4px' }}>
                            Useful for capturing survey responses (e.g. asking for city or email).
                          </small>
                        </div>
                      </div>
                    )}

                    {selectedNode.type === 'if_condition' && (
                      <div className="config-form-section">
                        <div className="form-group">
                          <label>Condition Logic <span style={{ color: 'red' }}>*</span></label>
                          <textarea
                            placeholder="e.g. {{name}} === 'Eshwar'"
                            value={selectedNode.config.condition || ''}
                            onChange={(e) => handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, condition: e.target.value })}
                          />
                          <small className="help-text">Use JavaScript logic. Return true or false.</small>
                        </div>
                      </div>
                    )}

                    {selectedNode.type === 'contact_has_tag' && (
                      <div className="config-form-section">
                        <div className="form-group">
                          <label>Select Tag <span style={{ color: 'red' }}>*</span></label>
                          <select
                            value={['customer', 'lead', 'vip', 'interested'].includes(selectedNode.config.tag) ? selectedNode.config.tag : (selectedNode.config.tag ? 'custom' : '')}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === 'custom') {
                                handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, tag: '' });
                              } else {
                                handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, tag: val });
                              }
                            }}
                          >
                            <option value="">-- Choose Tag --</option>
                            <option value="customer">Customer</option>
                            <option value="lead">Lead</option>
                            <option value="vip">VIP</option>
                            <option value="interested">Interested</option>
                            <option value="custom">Custom Tag...</option>
                          </select>
                        </div>
                        {(!['customer', 'lead', 'vip', 'interested'].includes(selectedNode.config.tag) || selectedNode.config.tag === 'custom') && (
                          <div className="form-group mt-2">
                            <label>Custom Tag Name <span style={{ color: 'red' }}>*</span></label>
                            <input
                              type="text"
                              placeholder="Type custom tag name..."
                              value={selectedNode.config.tag || ''}
                              onChange={(e) => handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, tag: e.target.value })}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {selectedNode.type === 'message_contains' && (
                      <div className="config-form-section">
                        <div className="form-group">
                          <label>Message Contains Keyword <span style={{ color: 'red' }}>*</span></label>
                          <input
                            type="text"
                            placeholder="Word or phrase to look for..."
                            value={selectedNode.config.condition || ''}
                            onChange={(e) => handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, condition: e.target.value })}
                          />
                        </div>
                      </div>
                    )}

                    {selectedNode.type === 'message_received' && (
                        <div className="config-form-section">
                            <div className="form-group">
                                <label>Trigger Keyword (Optional)</label>
                                <input
                                    type="text"
                                    placeholder="Leave empty for ALL messages"
                                    value={selectedNode.config.triggerMessage || ''}
                                    onChange={(e) => handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, triggerMessage: e.target.value })}
                                />
                                <small className="help-text">This flow will only start if the incoming message matches this keyword.</small>
                            </div>
                        </div>
                    )}

                    {selectedNode.type === 'webhook_received' && (
                        <div className="config-form-section">
                            <div className="form-group">
                                <label>Your Webhook URL</label>
                                <div className="webhook-url-display">
                                    <code>{`${window.location.origin}/api/hooks/flow/${selectedNode.id}`}</code>
                                    <button 
                                        className="copy-btn"
                                        onClick={() => {
                                            navigator.clipboard.writeText(`${window.location.origin}/api/hooks/flow/${selectedNode.id}`);
                                            toast.success('Webhook URL copied!');
                                        }}
                                    >
                                        <i className="fas fa-copy"></i>
                                    </button>
                                </div>
                                <small className="help-text">Send POST requests to this URL to trigger this automation.</small>
                            </div>
                        </div>
                    )}

                    {selectedNode.type === 'api_request' && (
                        <div className="config-form-section">
                            <div className="form-group">
                                <label>Endpoint URL <span style={{ color: 'red' }}>*</span></label>
                                <input
                                    type="text"
                                    placeholder="https://api.example.com/data"
                                    value={selectedNode.config.apiEndpoint || ''}
                                    onChange={(e) => handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, apiEndpoint: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Method</label>
                                <select
                                    value={selectedNode.config.apiMethod || 'POST'}
                                    onChange={(e) => handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, apiMethod: e.target.value })}
                                >
                                    <option value="GET">GET</option>
                                    <option value="POST">POST</option>
                                    <option value="PUT">PUT</option>
                                    <option value="DELETE">DELETE</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>JSON Body</label>
                                <textarea
                                    className="code-font"
                                    placeholder='{ "customer": "{{name}}" }'
                                    value={selectedNode.config.apiBody ? JSON.stringify(selectedNode.config.apiBody, null, 2) : ''}
                                    onChange={(e) => {
                                        try {
                                            const parsed = JSON.parse(e.target.value);
                                            handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, apiBody: parsed });
                                        } catch(err) {}
                                    }}
                                />
                            </div>
                        </div>
                    )}

                  {selectedNode.type === 'interactive_buttons' && (
                      <div className="config-form-section">
                        <div className="form-group">
                          <label>Header (Optional) <span className="char-count">{(selectedNode.config.header || '').length}/60</span></label>
                          <input
                            type="text"
                            value={selectedNode.config.header || ''}
                            onChange={(e) => handleNodeConfigChange(selectedNode.id, {
                              ...selectedNode.config,
                              header: e.target.value
                            })}
                            placeholder="Bold header text..."
                            maxLength="60"
                          />
                        </div>
                        <div className="form-group">
                          <label>Body (Message Content) <span style={{ color: 'red' }}>*</span> <span className="char-count">{(selectedNode.config.message || '').length}/1024</span></label>
                          <textarea
                            value={selectedNode.config.message || ''}
                            onChange={(e) => handleNodeConfigChange(selectedNode.id, {
                              ...selectedNode.config,
                              message: e.target.value
                            })}
                            placeholder="Enter message text..."
                            rows="3"
                            maxLength="1024"
                          />
                        </div>
                        <div className="form-group">
                          <label>Footer (Optional) <span className="char-count">{(selectedNode.config.footer || '').length}/60</span></label>
                          <input
                            type="text"
                            value={selectedNode.config.footer || ''}
                            onChange={(e) => handleNodeConfigChange(selectedNode.id, {
                              ...selectedNode.config,
                              footer: e.target.value
                            })}
                            placeholder="Small muted text..."
                            maxLength="60"
                          />
                        </div>
                        <div className="form-group">
                          <label>Buttons (Max 3)</label>
                          <div className="button-list-config">
                            {(selectedNode.config.buttons || []).map((btn, idx) => (
                              <div key={idx} className="button-config-item-expanded">
                                <div className="button-main-config">
                                  <select
                                    value={btn.type || 'reply'}
                                    onChange={(e) => {
                                      const newButtons = [...selectedNode.config.buttons];
                                      newButtons[idx].type = e.target.value;
                                      handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, buttons: newButtons });
                                    }}
                                    className="button-type-select"
                                  >
                                    <option value="reply">Quick Reply</option>
                                    <option value="url">Visit Website (CTA)</option>
                                    <option value="call">Call Number (CTA)</option>
                                  </select>
                                  <div className="button-input-wrapper">
                                    <input
                                      type="text"
                                      value={btn.text}
                                      onChange={(e) => {
                                        const newButtons = [...selectedNode.config.buttons];
                                        newButtons[idx].text = e.target.value.slice(0, 20);
                                        handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, buttons: newButtons });
                                      }}
                                      placeholder="Button label"
                                      maxLength="20"
                                    />
                                    <span className="char-counter">{btn.text.length}/20</span>
                                  </div>
                                  <button
                                    className="remove-btn icon-only"
                                    onClick={() => {
                                      const newButtons = selectedNode.config.buttons.filter((_, i) => i !== idx);
                                      handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, buttons: newButtons });
                                    }}
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </div>
                                {btn.type === 'url' && (
                                  <input
                                    type="text"
                                    className="button-extra-input"
                                    value={btn.url || ''}
                                    onChange={(e) => {
                                      const newButtons = [...selectedNode.config.buttons];
                                      newButtons[idx].url = e.target.value;
                                      handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, buttons: newButtons });
                                    }}
                                    placeholder="https://example.com"
                                  />
                                )}
                                {btn.type === 'call' && (
                                  <input
                                    type="text"
                                    className="button-extra-input"
                                    value={btn.phoneNumber || ''}
                                    onChange={(e) => {
                                      const newButtons = [...selectedNode.config.buttons];
                                      newButtons[idx].phoneNumber = e.target.value;
                                      handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, buttons: newButtons });
                                    }}
                                    placeholder="+1234567890"
                                  />
                                )}
                              </div>
                            ))}
                            {(selectedNode.config.buttons || []).length < 3 && (
                              <button
                                className="add-button-btn"
                                onClick={() => {
                                  const newButtons = [...(selectedNode.config.buttons || []), { id: `btn_${Date.now()}`, text: 'New Button', type: 'reply' }];
                                  handleNodeConfigChange(selectedNode.id, { ...selectedNode.config, buttons: newButtons });
                                }}
                              >
                                <i className="fas fa-plus"></i> Add Button
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {
                      (selectedNode.type === 'message_received' ||
                        selectedNode.type === 'webhook_received' ||
                        selectedNode.type === 'contact_added') && (
                        <div className="form-group">
                          <label>Trigger Keyword / Condition</label>
                          <input
                            type="text"
                            value={selectedNode.config.triggerMessage || ''}
                            onChange={(e) => handleNodeConfigChange(selectedNode.id, {
                              ...selectedNode.config,
                              triggerMessage: e.target.value
                            })}
                            placeholder="e.g. 'hello', 'start', '*'"
                          />
                          <small style={{ color: 'var(--color-text-secondary)', marginTop: '4px', display: 'block' }}>
                            Specify the keyword that triggers this flow.
                          </small>
                        </div>
                      )
                    }

                    {
                      selectedNode.type === 'wait_delay' && (
                        <div className="config-form-section">
                          <div className="form-group">
                            <label>Delay Amount</label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <input
                                type="number"
                                value={selectedNode.config.delay || ''}
                                onChange={(e) => handleNodeConfigChange(selectedNode.id, {
                                  ...selectedNode.config,
                                  delay: e.target.value
                                })}
                                placeholder="Value..."
                                style={{ flex: 1 }}
                              />
                              <select
                                value={selectedNode.config.unit || 'minutes'}
                                onChange={(e) => handleNodeConfigChange(selectedNode.id, {
                                  ...selectedNode.config,
                                  unit: e.target.value,
                                  delayUnit: e.target.value
                                })}
                                style={{ width: '100px' }}
                              >
                                <option value="seconds">Seconds</option>
                                <option value="minutes">Minutes</option>
                                <option value="hours">Hours</option>
                                <option value="days">Days</option>
                              </select>
                            </div>
                            <small style={{ color: 'var(--color-text-secondary)', marginTop: '4px', display: 'block' }}>
                              Wait before moving to the next node (Max 7 days).
                            </small>
                          </div>
                        </div>
                      )
                    }
                    {
                      selectedNode.type === 'add_tag' && (
                        <div className="form-group">
                          <label>Tag</label>
                          <input
                            type="text"
                            value={selectedNode.config.tag || ''}
                            onChange={(e) => handleNodeConfigChange(selectedNode.id, {
                              ...selectedNode.config,
                              tag: e.target.value
                            })}
                            placeholder="Enter tag name"
                          />
                        </div>
                      )
                    }
                  </div >
                </div >
              )}
            </div >
            
            {/* 🔥 Analytics Funnel Results Panel */}
            {isAnalyticsMode && (
              <div className="analytics-results-panel">
                <div className="analytics-panel-header">
                  <div className="panel-title">
                    <i className="fas fa-users"></i>
                    <h3>Filtered Contacts ({analyticsData?.recentJourneys?.length || 0})</h3>
                  </div>
                  <div className="active-filters-chips">
                    {activeFunnelFilters.map((f, i) => {
                      const fromNode = flowData.nodes.find(n => n.id === f.nodeId);
                      const portName = fromNode?.config?.buttons?.[f.fromPort]?.text || `Branch ${f.fromPort + 1}`;
                      return (
                        <div key={i} className="filter-chip">
                          <span>{fromNode?.name || 'Node'}: <strong>{portName}</strong></span>
                          <button onClick={() => toggleFunnelFilter(f.nodeId, f.fromPort)}>
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      );
                    })}
                    
                    {/* Interaction Filter Dropdown */}
                    <div className="filter-group">
                      <select 
                        className="interaction-filter-select"
                        onChange={(e) => {
                          if (e.target.value) {
                            const [nodeId, port] = e.target.value.split('|');
                            const filter = { nodeId, fromPort: parseInt(port) };
                            if (!activeFunnelFilters.some(f => f.nodeId === nodeId && f.fromPort === filter.fromPort)) {
                              const newFilters = [...activeFunnelFilters, filter];
                              setActiveFunnelFilters(newFilters);
                              fetchFlowAnalytics(currentFlow._id || currentFlow.id, newFilters);
                            }
                          }
                        }}
                        value=""
                      >
                        <option value="">+ Filter by Button Click...</option>
                        {flowData.nodes
                          .filter(n => ['interactive_buttons', 'list_messages', 'button_nodes'].includes(n.type))
                          .flatMap(n => {
                            const actions = [];
                            if (n.type === 'interactive_buttons') {
                              (n.config?.buttons || []).forEach((b, i) => {
                                actions.push({ label: `Btn: ${b.text}`, value: `${n.id}|${i}` });
                              });
                            } else if (n.type === 'list_messages') {
                              (n.config?.rows || []).forEach((r, i) => {
                                actions.push({ label: `List: ${r.title}`, value: `${n.id}|${i}` });
                              });
                            }
                            return actions;
                          })
                          .map(a => <option key={a.value} value={a.value}>{a.label}</option>)
                        }
                      </select>
                    </div>

                    {activeFunnelFilters.length > 0 && (
                      <div className="analytics-bulk-actions">
                        <button className="bulk-action-btn primary" onClick={async () => {
                          const contactIds = (analyticsData?.recentJourneys || []).map(j => j.contactId?._id).filter(Boolean);
                          if (contactIds.length === 0) return toast.info("No contacts to target");
                          
                          const tagName = prompt("Enter tag name to apply to these contacts:", "Targeted_Automation");
                          if (!tagName) return;

                          setLoadingAnalytics(true);
                          try {
                            const res = await requestHelpers.post('/contacts/bulk-tag', { 
                              contactIds, 
                              tag: tagName 
                            });
                            if (res.success) toast.success(`Applied tag '${tagName}' to ${res.count || contactIds.length} contacts`);
                          } catch (err) {
                            toast.error("Failed to apply tag");
                          }
                          setLoadingAnalytics(false);
                        }}>
                          <i className="fas fa-tag mr-1"></i> Tag Filtered Contacts
                        </button>
                        
                        <button className="clear-filters-btn" onClick={() => {
                          setActiveFunnelFilters([]);
                          fetchFlowAnalytics(currentFlow._id || currentFlow.id);
                        }}>
                          Clear All
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="analytics-panel-content">
                  <div className="contact-analytics-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Contact</th>
                          <th>Last Activity</th>
                          <th>Path Summary</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(analyticsData?.recentJourneys || []).map((j, idx) => (
                          <tr key={idx}>
                            <td>
                              <div className="contact-info-cell">
                                <span className="name">{j.contactId?.name || 'Unknown'}</span>
                                <span className="phone">{j.contactId?.phone}</span>
                              </div>
                            </td>
                            <td>{new Date(j.updatedAt).toLocaleString()}</td>
                            <td>
                              <div className="journey-summary-badges">
                                {j.path.slice(-3).map((p, pi) => {
                                  const node = flowData.nodes.find(n => n.id === p.nodeId);
                                  return (
                                    <span key={pi} className={`path-badge ${p.action}`}>
                                      {p.metadata?.label || node?.name || p.nodeType}
                                    </span>
                                  );
                                })}
                                {j.path.length > 3 && <span className="more">+ {j.path.length - 3}</span>}
                              </div>
                            </td>
                            <td>
                              <span className={`status-pill ${j.status}`}>{j.status}</span>
                            </td>
                            <td>
                              <button className="view-journey-btn" onClick={() => {
                                setSelectedJourney(j);
                                setShowJourneyModal(true);
                              }}>
                                View Detail
                              </button>
                            </td>
                          </tr>
                        ))}
                        {(analyticsData?.recentJourneys || []).length === 0 && (
                          <tr>
                            <td colSpan="5" className="empty-results">
                              No contacts match these specific filters. Try widening your selection.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Journey Detail Modal */}
            {showJourneyModal && selectedJourney && (
              <div className="journey-modal-overlay" onClick={() => setShowJourneyModal(false)}>
                <div className="journey-modal-container" onClick={e => e.stopPropagation()}>
                  <div className="journey-modal-header">
                    <div>
                      <h2>Customer Journey Timeline</h2>
                      <div className="contact-meta">
                        {selectedJourney.contactId?.name || 'Unknown'} • {selectedJourney.contactId?.phone}
                      </div>
                    </div>
                    <button className="close-modal-btn" onClick={() => setShowJourneyModal(false)}>
                      <i className="fas fa-times"></i>
                    </button>
                  </div>

                  <div className="journey-modal-body">
                    <div className="timeline-container">
                      {selectedJourney.path.map((step, sidx) => (
                        <div key={sidx} className={`timeline-item ${step.action}`}>
                          <div className="timeline-dot"></div>
                          <div className="timeline-content">
                            <div className="timeline-header">
                              <span className="node-type">{step.nodeType.replace(/_/g, ' ')}</span>
                              <span className="timestamp">{new Date(step.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                            <div className="timeline-label">
                              {step.metadata?.label || (step.action === 'entered' ? 'Entered Node' : 'Responded')}
                            </div>
                            {step.metadata?.interactionType && (
                              <div className="timeline-meta">
                                <i className="fas fa-reply mr-2"></i>
                                User {step.metadata.interactionType.replace(/_/g, ' ')}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div >
        )}
      </div >
    </DashboardLayout >
  );
};

export default Automation;
